import { BindAll } from 'lodash-decorators'
import type { CityTypeInfo, IRecommendCitiesResp, ICityDetailResp } from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiTonghao } from '@xt/client/api/kindred-spirit'
import { action, observable, computed, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { getStaticJson } from '@xt/client/utils/get-static-json'
import { PagePagination } from '@xt/client/utils/infinite-loading/index'
import { getCityCascader, type RawCityItem } from '@xt/client/dataSource/china/index'

type RecommendCityParamType = {
  adcode: string
  recommend?: boolean
  page: number
  pageSize: number
  statusArr: string
}

export enum EnumCityStatus {
  /** 未创建 */
  NotInited = 0,
  /** 助力中 */
  Assistance = 1,
  /** 已创建 */
  Inited = 2
}

@BindAll()
export class Guild {
  constructor() {}

  // 分桐列表
  @observable cities: Array<CityTypeInfo> = []
  // 当前定位城市分桐
  @observable locateCity: CityTypeInfo | null = null
  // 当前成员加入的分桐
  @observable joinedCity: CityTypeInfo | null = null

  pagePagination = new PagePagination({
    url: EnumApiTonghao.GuildList,
    needCache: false,
    needLastId: false,
    pageSize: 20,
    mapValues: res => {
      return {
        dataSource: this.transformCityInfo(res.data)
      }
    }
  })

  @computed
  get firstCity() {
    return this.recommendCities[0]
  }

  @computed
  get recommendCities() {
    const rawCities = toJS(this.cities)

    if (!this.locateCity?.id) {
      return toJS(rawCities).slice(1, 8)
    }

    const targetCity = toJS(this.locateCity)
    const locateId = targetCity.id
    const existIndex = rawCities.findIndex(item => item.id === locateId)

    if (existIndex > -1) {
      rawCities.splice(existIndex, 1)
    }
    rawCities.splice(1, 0, targetCity)

    return rawCities.slice(1, 8)
  }

  transformCityInfo(city: Array<IRecommendCitiesResp>): Array<CityTypeInfo> {
    return city.map(i => {
      let subTitle: string = ''

      if (i.memberCount || i.status === EnumCityStatus.Inited) {
        subTitle = `${i.memberCount}位桐学加入`
      } else if (i.recruitStatus === true) {
        subTitle = '准备中...'
      } else if (i.status === EnumCityStatus.Assistance) {
        subTitle = '即将开放...'
      }

      return {
        ...this.transformCityDetail(i),
        subTitle
      }
    })
  }

  transformCityDetail(city: ICityDetailResp): CityTypeInfo {
    return {
      id: city.id,
      count: city.memberCount,
      adcode: city.adcode,
      cityName: city.regionName,
      name: city.guildName,
      description: city.summary,
      cover: city.pcCoverPhoto,
      memberIds: city.previewMemberIds ?? [],
      status: city.status,
      recruitStatus: city.recruitStatus,
      appletAvatar: city.appletAvatar
    }
  }

  // 城市详情页数据初始化
  async initCityInfoById(guildId: string) {
    if (!guildId) return

    const [, res] = await $service<ICityDetailResp>({
      url: EnumApiTonghao.GuildDetail,
      params: {
        id: guildId
      }
    })

    if (res && res.id) {
      return this.transformCityDetail(res)
    }

    return false
  }
  // 获取推荐分桐
  public async getRecommendCities() {
    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.RecommendCitys,
      params: {
        page: 1,
        pageSize: 8
      }
    })

    if (res && Array.isArray(res.data) && res.data.length) {
      this.setCities(this.transformCityInfo(res.data))
    }
  }
  // 获取定位分桐
  public async getLocateRecommendCities(adcode: string) {
    const params: RecommendCityParamType = {
      adcode,
      page: 1,
      pageSize: 8,
      statusArr: [EnumCityStatus.Assistance, EnumCityStatus.Inited].join(',') // 1 助力中 2 已创建
    }

    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.GuildList,
      params
    })

    if (res && Array.isArray(res.data) && res.data.length === 1) {
      this.setLocateCity(this.transformCityInfo(res.data)[0])
    }
  }
  // 加载城市级联信息
  public async loadCascadeCities() {
    const data = await getStaticJson<RawCityItem[]>(`/mocks/geojson/cities-300.json`)

    if (!data) {
      reportEntryLog('topic.load-cascade-cities.error', {
        msg: 'fetch error'
      })
      return null
    }

    return getCityCascader(data)
  }
  // 获取当前用户加入的分桐
  public async getHasJoinedCity() {
    const [, res] = await $service<IRecommendCitiesResp>({
      url: EnumApiTonghao.Myself
    })

    if (res && res.id) {
      this.setJoinedCity(this.transformCityInfo([res])[0])
      return true
    }

    return false
  }

  // 加入桐学会
  public async joinGuild(guildId: number) {
    const [, res] = await $service<boolean>({
      url: EnumApiTonghao.GuildJoin,
      method: 'POST',
      data: {
        guildId
      }
    })

    return res === true
  }

  @action
  setJoinedCity(city: CityTypeInfo) {
    this.joinedCity = city
  }

  @action
  setLocateCity(city: CityTypeInfo) {
    this.locateCity = city
  }

  @action
  setCities(cities: Array<CityTypeInfo>) {
    this.cities = cities
  }

  @action
  reset() {
    this.cities = []
  }
}
